import { useEffect, useState } from 'react';
import UTIF from 'utif';

// Loyalty receipts can be stored as TIFF: the API deliberately does NOT convert
// TIFF to JPEG (unlike HEIC/HEIF) because Textract OCRs TIFF natively, so a
// .tiff upload keeps its format end to end. Browsers, though, have no native
// TIFF renderer — a plain <img src="…tiff"> just shows a broken image — so the
// admin queue and the review screen need to decode it client-side to preview it.

const TIFF_EXTENSIONS = new Set(['tif', 'tiff']);

const extensionOf = (name) => (name || '').split('.').pop()?.toLowerCase();

export const isTiffFile = (name) => TIFF_EXTENSIONS.has(extensionOf(name));

/**
 * Fetches a TIFF, decodes its first page with UTIF (pure JS — no native binding
 * or wasm, matches the "no arch-specific deps" constraint the API's HEIC
 * conversion notes call out), paints it to a canvas and returns a PNG object
 * URL a normal <img> can display. Rejects on any fetch/decode failure so the
 * caller can fall back to the "open the original file" affordance.
 * @param {string} url
 * @returns {Promise<string>} object URL for an image/png Blob
 */
export async function decodeTiffToObjectUrl(url) {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`Failed to fetch TIFF: ${res.status}`);
    const buffer = await res.arrayBuffer();

    const ifds = UTIF.decode(buffer);
    if (!ifds || ifds.length === 0) throw new Error('TIFF contains no pages');
    const page = ifds[0];
    UTIF.decodeImage(buffer, page, ifds);

    const { width, height } = page;
    if (!width || !height) throw new Error('TIFF page has no readable dimensions');

    const rgba = UTIF.toRGBA8(page); // Uint8Array, width * height * 4
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba), width, height), 0, 0);

    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob(
            (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
            'image/png',
        );
    });
    return URL.createObjectURL(blob);
}

// Decoding a TIFF (fetch + full-page decode) is expensive, and the same
// receipt's URL is rendered repeatedly — the queue thumbnail, the preview
// modal, pagination round-trips, the review screen. Cache the decode per URL
// so each TIFF is fetched and decoded at most once. Bounded (LRU) so an admin
// paging through a large batch of TIFF receipts in one sitting can't leak an
// unbounded number of object URLs — the oldest entry is revoked and dropped
// once the cap is hit.
const MAX_CACHED_DECODES = 20;
const decodeCache = new Map(); // url -> Promise<objectUrl>, insertion-ordered

export function getTiffObjectUrl(url) {
    if (decodeCache.has(url)) {
        // Refresh LRU recency.
        const promise = decodeCache.get(url);
        decodeCache.delete(url);
        decodeCache.set(url, promise);
        return promise;
    }

    const promise = decodeTiffToObjectUrl(url).catch((err) => {
        decodeCache.delete(url); // let a later attempt retry a transient failure
        throw err;
    });
    decodeCache.set(url, promise);

    while (decodeCache.size > MAX_CACHED_DECODES) {
        const oldestUrl = decodeCache.keys().next().value;
        const oldest = decodeCache.get(oldestUrl);
        decodeCache.delete(oldestUrl);
        Promise.resolve(oldest).then(URL.revokeObjectURL, () => {});
    }

    return promise;
}

/**
 * Resolves a receipt file URL to something an <img> can render.
 *  - non-TIFF (or empty): passthrough — { src: url, status: 'ready' | 'idle' }
 *  - TIFF: decodes (cached) in the background — 'loading' → 'ready' | 'error'
 * @param {string|null|undefined} url
 * @returns {{ src: string|null, status: 'idle'|'loading'|'ready'|'error' }}
 */
export function useTiffPreview(url) {
    const [state, setState] = useState(
        () => (url && !isTiffFile(url)
            ? { src: url, status: 'ready' }
            : { src: null, status: url ? 'loading' : 'idle' }),
    );

    useEffect(() => {
        if (!url) {
            setState({ src: null, status: 'idle' });
            return undefined;
        }
        if (!isTiffFile(url)) {
            setState({ src: url, status: 'ready' });
            return undefined;
        }

        let cancelled = false;
        setState({ src: null, status: 'loading' });
        getTiffObjectUrl(url)
            .then((decoded) => {
                if (!cancelled) setState({ src: decoded, status: 'ready' });
            })
            .catch(() => {
                if (!cancelled) setState({ src: null, status: 'error' });
            });

        return () => { cancelled = true; };
    }, [url]);

    return state;
}
